import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Chip, Button, TextField, Divider } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ChatIcon from '@mui/icons-material/Chat';
import { useKBTheme } from '../../hooks/useKBTheme';
import { mockTickets } from '../../data/mockData';

export const AnimatedTicketDetailDemo: React.FC = () => {
  const { mode } = useKBTheme();
  const [ticket] = useState({ ...mockTickets[0], category: 'Software Installation', requester: 'user@company.com' } as any);
  const [isEditing, setIsEditing] = useState(false);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    setTimeout(() => setShowContent(true), 300);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return '#6366f1';
      case 'in_progress': return '#8b5cf6';
      case 'resolved': return '#10b981';
      case 'closed': return '#64748b';
      default: return '#64748b';
    }
  };

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', background: mode === 'dark' ? '#0f172a' : '#f8fafc', p: 3 }}>
      {/* Header Card */}
      <Card
        sx={{
          mb: 3,
          borderRadius: 4,
          background: mode === 'dark'
            ? 'linear-gradient(135deg, rgba(15, 15, 15, 0.95) 0%, rgba(20, 20, 20, 0.95) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
          border: `1px solid ${mode === 'dark' ? 'rgba(99, 102, 241, 0.2)' : 'rgba(226, 232, 240, 0.8)'}`,
          opacity: showContent ? 1 : 0,
          transform: showContent ? 'translateY(0)' : 'translateY(20px)',
          transition: 'all 0.4s ease-out',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: mode === 'dark' ? '#fff' : '#0f172a' }}>
                {ticket.summary}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Chip
                  label={ticket.status.replace('_', ' ').toUpperCase()}
                  sx={{
                    bgcolor: getStatusColor(ticket.status),
                    color: '#fff',
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label={`Priority: ${ticket.priority}`}
                  sx={{
                    bgcolor: ticket.priority === 'high' ? '#ef4444' : ticket.priority === 'medium' ? '#f59e0b' : '#10b981',
                    color: '#fff',
                  }}
                />
                <Chip label={(ticket as any).category || 'General'} />
              </Box>
            </Box>
            <Button
              startIcon={isEditing ? <SaveIcon /> : <EditIcon />}
              onClick={() => setIsEditing(!isEditing)}
              sx={{
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                '&:hover': { background: 'linear-gradient(135deg, #4f46e5, #7c3aed)' },
              }}
            >
              {isEditing ? 'Save' : 'Edit'}
            </Button>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Details Grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 3 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PersonIcon sx={{ fontSize: 18, color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b' }} />
                <Typography variant="caption" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b' }}>
                  Created By
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 600, color: mode === 'dark' ? '#fff' : '#0f172a' }}>
                {ticket.requester}
              </Typography>
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <GroupIcon sx={{ fontSize: 18, color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b' }} />
                <Typography variant="caption" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b' }}>
                  Assigned Team
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 600, color: mode === 'dark' ? '#fff' : '#0f172a' }}>
                {ticket.assigned_to || 'Unassigned'}
              </Typography>
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AccessTimeIcon sx={{ fontSize: 18, color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b' }} />
                <Typography variant="caption" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b' }}>
                  Created
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 600, color: mode === 'dark' ? '#fff' : '#0f172a' }}>
                {new Date(ticket.created_at).toLocaleDateString()}
              </Typography>
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AccessTimeIcon sx={{ fontSize: 18, color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b' }} />
                <Typography variant="caption" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b' }}>
                  Estimated Resolution
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 600, color: mode === 'dark' ? '#fff' : '#0f172a' }}>
                1-2 business days
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Description */}
      <Card
        sx={{
          mb: 3,
          borderRadius: 3,
          background: mode === 'dark' ? 'rgba(15, 15, 15, 0.5)' : 'rgba(255, 255, 255, 0.8)',
          border: `1px solid ${mode === 'dark' ? 'rgba(71, 85, 105, 0.3)' : 'rgba(226, 232, 240, 0.8)'}`,
          opacity: showContent ? 1 : 0,
          transform: showContent ? 'translateY(0)' : 'translateY(20px)',
          transition: 'all 0.4s ease-out 0.1s',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: mode === 'dark' ? '#fff' : '#0f172a' }}>
            Description
          </Typography>
          <Typography variant="body2" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569', lineHeight: 1.7 }}>
            {ticket.description}
          </Typography>
        </CardContent>
      </Card>

      {/* Comments Section */}
      <Card
        sx={{
          borderRadius: 3,
          background: mode === 'dark' ? 'rgba(15, 15, 15, 0.5)' : 'rgba(255, 255, 255, 0.8)',
          border: `1px solid ${mode === 'dark' ? 'rgba(71, 85, 105, 0.3)' : 'rgba(226, 232, 240, 0.8)'}`,
          opacity: showContent ? 1 : 0,
          transform: showContent ? 'translateY(0)' : 'translateY(20px)',
          transition: 'all 0.4s ease-out 0.2s',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ChatIcon sx={{ fontSize: 20, color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: mode === 'dark' ? '#fff' : '#0f172a' }}>
              Comments
            </Typography>
          </Box>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Add a comment..."
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                background: mode === 'dark' ? 'rgba(15, 15, 15, 0.5)' : 'rgba(248, 250, 252, 0.8)',
              },
            }}
          />
          <Button
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              '&:hover': { background: 'linear-gradient(135deg, #4f46e5, #7c3aed)' },
            }}
          >
            Post Comment
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

